import React, {useEffect, useMemo, useState} from 'react';

import {Subscription} from 'src/components/subscription/subscription';
import {UsersInfoController} from 'src/modules/users-info/users-info.controller';
import {
    getSubscriptionsByEventId,
    getSubscriptionsByEventIdSuccess,
    removeEventSubscriptionById,
    removeEventSubscriptionByIdSuccess,
    saveEventSubscription,
    saveEventSubscriptionSuccess,
} from 'src/openapi/apis/EventSubscriptionControllerApi';
import {EventSubscription} from 'src/openapi/models/EventSubscription';
import {useAppDispatch} from 'src/store';

import {LocalStorage} from '@pb/kit-util-client-storage';

type Props = {
    eventId: number | null,
};

const SUBSCRIPTION_TITLE = 'WATCHING THIS EVENT';

export const EventSubscriptionDropdown = ({eventId}: Props) => {
    const dispatch = useAppDispatch();
    const userEmail = LocalStorage.get('user')?.email || '';
    const [subscribed, setSubscribed] = useState(false);
    const [shownSubscription, setShownSubscription] = useState(false);
    const [shownAutoSuggest, setShownAutoSuggest] = useState(false);
    const [subscriptions, setSubscriptions] = useState<Array<EventSubscription>>([]);

    const watchersEmails = useMemo(() => {
        return [...new Set(subscriptions.map(subscription => subscription.userEmail))];
    }, [subscriptions]);

    useEffect(() => {
        if (eventId) {
            dispatch(getSubscriptionsByEventId({eventId})).then(e => {
                if (!e.payload.error && e.type === getSubscriptionsByEventIdSuccess.type) {
                    setSubscriptions(e.payload.data);
                }
            });
        }
    }, [eventId, dispatch]);

    useEffect(() => {
        setSubscribed(watchersEmails.includes(userEmail));
    }, [userEmail, watchersEmails]);

    const saveSubscription = (email: string) => {
        if (watchersEmails.includes(email)) {
            return;
        }

        if (eventId) {
            const eventSubscription: EventSubscription = {
                eventId,
                userEmail: email,
            };

            dispatch(saveEventSubscription({eventSubscription})).then(e => {
                if (!e.payload.error && e.type === saveEventSubscriptionSuccess.type) {
                    const savedId = e.payload.data;
                    setSubscriptions(prevWatchers => [...prevWatchers,
                        {id: savedId, ...eventSubscription},
                    ]);

                    if (email === userEmail) {
                        setSubscribed(true);
                    }
                }
            });
        }
    };

    const onWatcherRemove = (id: number) => {
        dispatch(removeEventSubscriptionById({id})).then(e => {
            if (!e.payload.error && e.type === removeEventSubscriptionByIdSuccess.type) {
                setSubscriptions(prevWatchers => prevWatchers.filter(item => item.id !== id));
                setShownAutoSuggest(false);
            }
        });
    };

    return (
        <>
            <UsersInfoController emails={watchersEmails}/>
            <Subscription
                title={SUBSCRIPTION_TITLE}
                subscribed={subscribed}
                disabled={!eventId}
                shownAutoSuggest={shownAutoSuggest}
                shownSubscription={shownSubscription}
                userEmail={userEmail}
                watchers={subscriptions}
                watchersEmails={watchersEmails}
                onWatcherRemove={onWatcherRemove}
                setShown={setShownSubscription}
                setShownAutoSuggest={setShownAutoSuggest}
                setSubscribed={setSubscribed}
                saveSubscription={saveSubscription}
            />
        </>
    );
};
